// Registro de rotas por agente (Onda 1 / A02): cada agente cria `rotas/<id>.tsx`
// exportando `RouteObject[]` (default export) e concatena aqui, no fim dos filhos
// do layout protegido — sem editar as rotas dos demais. As rotas novas do Figma e
// os redirects das rotas antigas do A02 já vivem em `rotas/a02.tsx` (dados do mapa
// em `./redirects.ts`). Conflito de path é erro de integração: o coordenador resolve.
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom'
import { FC } from 'react'
import { MainLayout } from '@/components/layout/MainLayout'
import { PrivateRoute } from './PrivateRoute'
import rotasA02 from './rotas/a02'
import rotasA04 from './rotas/a04'
import rotasA05 from './rotas/a05'
import rotasA07 from './rotas/a07'
import rotasA09 from './rotas/a09'
import rotasA10 from './rotas/a10'
import rotasA11 from './rotas/a11'
import rotasA12 from './rotas/a12'
import rotasA13 from './rotas/a13'
import rotasA14 from './rotas/a14'
import rotasA15 from './rotas/a15'
import rotasA16 from './rotas/a16'
import rotasA17 from './rotas/a17'
import rotasPipeDev5 from './rotas/pipeDev5'
import rotasVcDev4 from './rotas/vcDev4'
import rotasAn8 from './rotas/an8'
import rotasAn10 from './rotas/an10'
import rotasL17 from './rotas/l17'
import rotasPp6 from './rotas/pp6'
import rotasConsultores from './rotas/consultores'

// Auth (named export)
import { LoginPage } from '@/pages/auth/LoginPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'

// Dashboard (named export)
import { DashboardPage } from '@/pages/dashboard/DashboardPage'

// Oportunidades (default exports)
import OportunidadesListPage from '@/pages/oportunidades/OportunidadesListPage'
import OportunidadeDetailPage from '@/pages/oportunidades/OportunidadeDetailPage'
import OportunidadeFormPage from '@/pages/oportunidades/OportunidadeFormPage'

// Financeiro (default export)
import FinanceiroPage from '@/pages/financeiro/FinanceiroPage'

// Notas Fiscais (default export)

// Produtos (default exports) — a lista vive em /produtos-materiais (A02)
import ProdutoDetailPage from '@/pages/produtos/ProdutoDetailPage'
import ProdutoFormPage from '@/pages/produtos/ProdutoFormPage'

// Documentos (default export) — a lista vive em /pendencias-documentos e
// /documentos-materiais (A02)
import DocumentoDetailPage from '@/pages/documentos/DocumentoDetailPage'

// IA (default export)
import BuscaIAPage from '@/pages/ia/BuscaIAPage'

// Estruturação (default export)

// Defesa de Crédito (default export)
import DefesaCreditoPage from '@/pages/defesa/DefesaCreditoPage'
import PendenciasPage from '@/pages/pendencias/PendenciasPage'

// Funil de entrada de consultores (PDF, Etapa 1)
import QueroSerConsultorPage from '@/pages/candidatura/QueroSerConsultorPage'
import CadastroConvitePage from '@/pages/candidatura/CadastroConvitePage'
import PrimeiroAcessoPage from '@/pages/onboarding/PrimeiroAcessoPage'

// Campanhas (gestão de metas comerciais — ADMIN/GERENTE)
import CampanhasPage from '@/pages/campanhas/CampanhasPage'


// LGPD (públicas)
import PrivacidadePage from '@/pages/lgpd/PrivacidadePage'
import TermosPage from '@/pages/lgpd/TermosPage'
import DpoPage from '@/pages/lgpd/DpoPage'
import RopaPage from '@/pages/lgpd/RopaPage'

const ProtectedLayout: FC = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
)

const NotFoundPage: FC = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '16px',
    fontFamily: '"DM Sans", Inter, sans-serif',
    background: '#F0F0F2',
  }}>
    <h1 style={{ fontSize: '4rem', margin: 0, color: '#ff620c', letterSpacing: '-0.04em' }}>404</h1>
    <p style={{ color: '#6B7280', letterSpacing: '-0.01em' }}>Página não encontrada</p>
    <a href="/" style={{ color: '#ff620c', fontWeight: 600 }}>Voltar ao início</a>
  </div>
)

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  // "Quero ser um Consultor" — pública: quem se candidata ainda não tem conta.
  {
    path: '/quero-ser-consultor',
    element: <QueroSerConsultorPage />,
  },
  // Cadastro por convite — público: é este envio que cria a conta. Quem autoriza
  // é o token do convite, não um login.
  {
    path: '/convite/:token',
    element: <CadastroConvitePage />,
  },
  // LGPD — páginas públicas (acessíveis sem autenticação)
  {
    path: '/privacidade',
    element: <PrivacidadePage />,
  },
  {
    path: '/termos',
    element: <TermosPage />,
  },
  {
    path: '/dpo',
    element: <DpoPage />,
  },
  {
    path: '/lgpd/ropa',
    element: <RopaPage />,
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      // Clientes — detalhe/edição: `rotas/vcDev4.tsx` (só back-office, VC-08).
      // Primeiro acesso (PDF, Etapa 4). Dentro do layout autenticado: quem está em
      // onboarding já tem conta — é o PrivateRoute que desvia para cá.
      {
        path: 'primeiro-acesso',
        element: <PrimeiroAcessoPage />,
      },
      // Financeiro
      {
        path: 'financeiro',
        element: <FinanceiroPage />,
      },
      // Notas Fiscais: absorvida pelo Financeiro (`/financeiro?aba=nf`); redirect em `rotas/a15.tsx`.
      // Estruturação: registrada em `rotas/a09.tsx` (por persona; back-office segue na EstruturacaoPage).
      // Defesa de Crédito — vinculada a uma reserva
      {
        path: 'reservas/:id/defesa',
        element: <DefesaCreditoPage />,
      },
      // Pendências — vinculadas a uma reserva
      {
        path: 'reservas/:id/pendencias',
        element: <PendenciasPage />,
      },
      // Campanhas — metas comerciais (AN-8: CRUD só ADMIN; participantes ADMIN e
      // GERENTE+GESTOR_EQUIPE; demais back-office só leitura)
      {
        path: 'campanhas',
        element: <CampanhasPage />,
      },
      // Oportunidades (legado)
      {
        path: 'oportunidades',
        element: <OportunidadesListPage />,
      },
      {
        path: 'oportunidades/nova',
        element: <OportunidadeFormPage />,
      },
      {
        path: 'oportunidades/:id',
        element: <OportunidadeDetailPage />,
      },
      {
        path: 'oportunidades/:id/editar',
        element: <OportunidadeFormPage />,
      },
      // Produtos — detalhe/edição seguem aqui; a lista virou /produtos-materiais (A02)
      {
        path: 'produtos/novo',
        element: <ProdutoFormPage />,
      },
      {
        path: 'produtos/:id/editar',
        element: <ProdutoFormPage />,
      },
      {
        path: 'produtos/:id',
        element: <ProdutoDetailPage />,
      },
      // Documentos — detalhe segue aqui; as listas viraram rotas do A02
      {
        path: 'documentos/:id',
        element: <DocumentoDetailPage />,
      },
      // IA
      {
        path: 'ia/busca',
        element: <BuscaIAPage />,
      },
      // Placeholders (notificacoes saiu daqui: ver rotas/a14.tsx)
      {
        path: 'configuracoes',
        element: <div style={{ padding: 32 }}>Em desenvolvimento — Configurações</div>,
      },
      // Rotas registradas por agente (ver comentário no topo do arquivo)
      ...rotasA02,
      ...rotasA04,
      ...rotasA05,
      ...rotasA07,
      ...rotasA09,
      ...rotasA10,
      ...rotasA11,
      ...rotasA12,
      ...rotasA13,
      ...rotasA14,
      ...rotasA15,
      ...rotasA16,
      ...rotasA17,
      // Plano Pipeline/Simulações (Rodada 2): políticas de simulação (ADMIN).
      ...rotasPipeDev5,
      // Visão do Consultor, Onda 1 (DEV-4): detalhe/edição de cliente só do back-office.
      ...rotasVcDev4,
      // Visão do Consultor, Onda 4 (AN-8 / DEV-P): equipes do gerente e ranking com escopo.
      ...rotasAn8,
      // Visão do Consultor, Onda 5 (AN-10 / DEV-R): nível do consultor (página Níveis).
      ...rotasAn10,
      // Visão do Consultor, Onda 5 (AN-12 / L17 — DEV-AB): privacidade (ADMIN e DPO).
      ...rotasL17,
      // Políticas por Produto, P6 (AN-PP6 / DEV-PP6-F1): fila de aprovação das políticas do produto.
      ...rotasPp6,
      // Tela de Consultores (ADMIN — docs/PLANO_TELA_CONSULTORES.md).
      ...rotasConsultores,
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
])

export const Router: FC = () => <RouterProvider router={router} />
